import includes from 'lodash/includes';
import map from 'lodash/map';

// Type definitions
type SchemaType = 'ApartmentComplex' | 'Apartment' | 'SingleFamilyResidence' | 'Room' | 'Place';
type AnswerType = 'answer' | 'orderedList' | 'unorderedList';

interface Address {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface Geo {
  lat?: number;
  lon?: number;
}

interface PlaceParams {
  address?: Address;
  geo?: Geo;
  type?: string;
  rooms?: number;
}

interface PlaceSchema {
  '@type': string;
  address: {
    '@type': string;
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry: string;
  };
  geo: {
    '@type': string;
    latitude?: number;
    longitude?: number;
  };
  numberOfRooms?: number;
}

interface Listing {
  address?: Address;
  active?: boolean;
}

interface AnswerResponse {
  '@type': string;
  text: string | Array<string>;
}

const schemaUtils = {
  getFormattedReviewerType(reviewerType: string): string {
    if (reviewerType === 'FUTURE_RESIDENT') {
      return 'Future Resident';
    } else if (reviewerType === 'CURRENT_RESIDENT') {
      return 'Current Resident';
    } else if (reviewerType === 'FORMER_RESIDENT') {
      return 'Former Resident';
    } else {
      return '';
    }
  },
  getSchemaType(listingType: string): SchemaType {
    switch (listingType) {
      case 'large':
      case 'medium':
      case 'garden':
        return 'ApartmentComplex';
      case 'condo':
      case 'divided':
        return 'Apartment';
      case 'townhouse':
      case 'house':
        return 'SingleFamilyResidence';
      case 'room':
        return 'Room';
      default:
        return 'Place';
    }
  },
  getPlace({ address = {}, geo = {}, type = 'Place', rooms }: PlaceParams = {}): PlaceSchema {
    const { street, city, state, zip } = address;
    const { lat, lon } = geo;

    const placeSchema: PlaceSchema = {
      '@type': type,
      address: {
        '@type': 'PostalAddress',
        streetAddress: street,
        addressLocality: city,
        addressRegion: state,
        postalCode: zip,
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: lat,
        longitude: lon,
      },
    };

    if (includes(['Apartment', 'SingleFamilyResidence'], type) && rooms) {
      placeSchema.numberOfRooms = rooms;
    }

    return placeSchema;
  },
  getAvailability(listing: Listing = {}): string | null {
    let availability = null;
    const includeAvailability =
      listing &&
      listing.address &&
      listing.address.state &&
      includes(['FL', 'NJ', 'DC', 'AZ', 'OR', 'MN', 'MS', 'RI', 'MT', 'VT'], listing.address.state);

    if (includeAvailability) {
      availability = listing && listing.active ? 'LimitedAvailability' : 'OutOfStock';
    }

    return availability;
  },
  getFormattedFaqAnswer(
    answer: string | Array<string>,
    answerType: AnswerType,
    listHeader: string | null = null,
  ): AnswerResponse {
    const listText = (type: string, header: string | null): string => {
      const ListElement = type === 'unorderedList' ? 'ul' : 'ol';

      if (header) {
        return `${header}<${ListElement}>${map(answer as Array<string>, (item: string) => {
          return `<li>${item}</li>`;
        }).join('')}</${ListElement}>`;
      }

      return `<${ListElement}>${map(answer as Array<string>, (item: string) => {
        return `<li>${item}</li>`;
      }).join('')}</${ListElement}>`;
    };

    return answerType === 'answer'
      ? {
          '@type': 'Answer',
          text: answer as string,
        }
      : {
          '@type': 'Answer',
          text: listText(answerType, listHeader),
        };
  },
};

export default schemaUtils;
